import { useState, useEffect, useCallback } from 'react';
import { cx } from '@linaria/core';
import { either } from 'fp-ts';
import { Form, Formik } from 'formik';
import { Button } from 'components/ui_library/Button';
import { InputField } from 'components/ui_library/Formik';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { errorToReactLeft, pipe } from 'utils/fp';
import { reactLeftToJSX } from 'utils/uiStates/uiStates';
import { fetchUserApiScopesTE } from 'api/keys/fetchUserApiScopes';
import { getScopeAndEndpoint } from 'modules/Apps/deprecatedScopeGraphLogic/scope';
import type { TUserKeysMST } from 'modules/Apps/mstStores/UserKeysMST';
import { APIKeyType } from 'modules/AppSettings/ApiKeysTable/constants';
import { flex } from 'styles/utils';
import { dialogWrapper, formContainer } from './styles';
import { ScopesList } from '../ScopesList/ScopesList';

type UpdateApiKeyModalProps = {
  store: TUserKeysMST;
  onClose: () => void;
  type: CF.API.ApiKeys.KeyType;
  userId: string;
  appId?: string;
  apiKeyId: string;
  apiKey: CF.API.ApiKeys.KeyData;
};

interface FormData {
  tokenDescription: string;
}

export const UpdateApiKeyModal = ({ store, onClose, type, userId, appId, apiKeyId, apiKey }: UpdateApiKeyModalProps): JSX.Element => {
  const { data: scopesTE } = useQueryTE(
    [queryKeys.Scopes, { userId, keyType: type }],
    fetchUserApiScopesTE({ userId, keyType: type }, errorToReactLeft),
  );
  const [scopesEntity, setScopesEntity] = useState<string[]>([]);
  const [currentScopes, setCurrentScopes] = useState<string[]>([]);

  useEffect(() => {
    const data = (apiKey.scopes || []).concat(apiKey.endpoints || []);
    setScopesEntity(data);
    setCurrentScopes(data);
  }, [apiKey]);

  const onScopesChanged = useCallback((newScopes: string[]): void => {
    setScopesEntity(newScopes);
  }, []);

  const onSubmit = useCallback(
    (values: FormData) => {
      const { scopes, endpoints } = getScopeAndEndpoint(scopesEntity);
      store.updateKey({
        type: APIKeyType,
        userId,
        appId,
        keyId: apiKeyId,
        scopes,
        endpoints,
        description: values.tokenDescription,
      });

      onClose();
    },
    [store, userId, apiKeyId, scopesEntity, onClose, appId],
  );

  return (
    <div className={dialogWrapper}>
      <div className={formContainer}>
        <Formik initialValues={{ tokenDescription: apiKey?.description || '' } as FormData} onSubmit={onSubmit}>
          <Form>
            <div>
              <div className="form-field">
                <div className="field-name">Token Description</div>
                <InputField type="text" placeholder="Short descriptive name" name="tokenDescription" />
              </div>

              {pipe(
                scopesTE,
                either.fold(reactLeftToJSX(), ({ scope_deps, endpoint_deps }) => (
                  <ScopesList existingScopes={currentScopes} scopeDeps={scope_deps} endpointDeps={endpoint_deps} onScopesChanged={onScopesChanged} />
                )),
              )}

              <div className={cx(flex.init, flex.gap[1], flex.justifyContent.end)}>
                <Button variant="tertiary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={scopesEntity.length === 0}>
                  Update
                </Button>
              </div>
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
};
